import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of, forkJoin } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { AppState } from '..';
import { AppConfig } from '../../tamalelibs/models/app-config.model';
import { DataList } from '../../tamalelibs/models/data-list.model';
import { Thread } from '../../tamalelibs/models/thread.model';
import { ThreadService } from '../../tamalelibs/services/thread.service';
import { DraftViewActionTypes } from '../actions/draft-view.actions';

@Injectable()
export class DraftViewEffects {
    constructor(
        private _actions$: Actions,
        private _store: Store<AppState>,
        private _threadService: ThreadService,
    ) { }

    reloadDraftListEffect$: Observable<any> = createEffect(() => this._actions$.pipe(
        ofType(DraftViewActionTypes.RELOAD),
        map((action: any) => action.payload),
        switchMap(payload => {
            const pageIndex = payload.pageIndex;
            const pageSize = payload.pageSize;
            let sortBy = '';
            switch (payload.sortBy) {
                case 'submittedDate':
                    sortBy = 'submitteddateofthread';
                    break;
                case 'lastModifiedDate':
                    sortBy = 'lastediteddateofthread';
                    break;
                case 'displayDate':
                    sortBy = 'displaydateofthread';
                    break;
                default:
                    sortBy = payload.sortBy;
            }

            const sortOrder = payload.sortOrder || 'desc';

            return this._threadService.queryByParams(pageIndex, pageSize, sortBy, sortOrder, AppConfig.defaultAdvFilter, [], payload.searchText, true, '').pipe(
                map(response => {
                    const threadList = this._parseThreadList(response);
                    return {
                        type: DraftViewActionTypes.RELOAD_SUCCESS,
                        payload: threadList
                    };
                }),
                catchError(err => {
                    return of({
                        type: DraftViewActionTypes.RELOAD_FAILED,
                        payload: err.message
                    });
                })
            );
        })
    ));

    loadMoreDraftListEffect$: Observable<Action> = createEffect(() => this._actions$.pipe(
        ofType(DraftViewActionTypes.LOAD_MORE),
        map((action: any) => action.payload),
        switchMap(payload => {
            const pageIndex = payload.pageIndex;
            const pageSize = payload.pageSize;
            let sortBy = '';
            switch (payload.sortBy) {
                case 'submittedDate':
                    sortBy = 'submitteddateofthread';
                    break;
                case 'lastModifiedDate':
                    sortBy = 'lastediteddateofthread';
                    break;
                case 'displayDate':
                    sortBy = 'displaydateofthread';
                    break;
                default:
                    sortBy = payload.sortBy;
            }

            const sortOrder = payload.sortOrder || 'desc';

            const observables$ = [];
            observables$.push(this._threadService.queryByParams(pageIndex - 1, pageSize, sortBy, sortOrder, AppConfig.defaultAdvFilter, [], payload.searchText, true, ''));
            observables$.push(this._threadService.queryByParams(pageIndex, pageSize, sortBy, sortOrder, AppConfig.defaultAdvFilter, [], payload.searchText, true, ''));

            return forkJoin(observables$).pipe(
                map((responses: any) => {
                    // combine two response
                    const response = {
                        count: responses[1].count,
                        next: responses[1].next
                    };
                    response['thread-list'] = responses[0]['thread-list'].concat(responses[1]['thread-list']);
                    return response;
                }),
                map(response => {
                    const threadList = this._parseThreadList(response);
                    return ({
                        type: DraftViewActionTypes.LOAD_MORE_SUCCESS,
                        payload: {
                            pageIndex: pageIndex,
                            pageSize: pageSize,
                            threads: threadList
                        }
                    });
                })
            );
        })
    ));

    private _parseThreadList(response) {
        const threadList: DataList<Thread> = new DataList<Thread>();

        threadList.totalCount = response.count;
        threadList.hasNext = response.next != null;
        response['thread-list'].forEach(element => {
            const thread = Thread.parse(element);
            threadList.push(thread);
        });

        return threadList;
    }
}
